import React, { useEffect, useState } from 'react';
import {
  fetchWebPushStatus,
  getCurrentWebPushEndpoint,
  repairWebPush,
  sendWebPushTest,
  subscribeWebPush,
} from '../webpush';
import './SearchBar.css';

const BrowserAlertStatus = ({ paid = false, email = '', compact = false }) => {
  const [state, setState] = useState('checking');
  const [endpoint, setEndpoint] = useState('');
  const [error, setError] = useState('');
  const [testState, setTestState] = useState('idle');
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    if (!paid || !email || typeof window === 'undefined') return;
    let cancelled = false;
    (async () => {
      try {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          if (!cancelled) setState('unsupported');
          return;
        }
        if (window.Notification.permission === 'denied') {
          if (!cancelled) setState('permission_blocked');
          return;
        }
        const currentEndpoint = await getCurrentWebPushEndpoint();
        const result = await fetchWebPushStatus(email, currentEndpoint);
        if (cancelled) return;
        setEndpoint(currentEndpoint);
        setState(result?.state === 'verified'
          ? 'verified'
          : (result?.state === 'enrolled' || result?.state === 'active')
            ? 'enrolled'
            : 'not_enabled');
        setError('');
      } catch (statusError) {
        if (!cancelled) {
          setState('error');
          setError(statusError?.response?.data?.error || 'Browser alert status could not be checked.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [paid, email]);

  const handleEnable = async () => {
    setError('');
    setState('checking');
    try {
      const result = await subscribeWebPush(email);
      if (!result?.ok) throw new Error('Browser notification registration was not confirmed.');
      const status = await fetchWebPushStatus(email, result.endpoint);
      setEndpoint(result.endpoint);
      const enrolled = status?.state === 'enrolled' || status?.state === 'verified' || status?.state === 'active';
      setState(status?.state === 'verified' ? 'verified' : enrolled ? 'enrolled' : 'error');
      if (!enrolled) throw new Error('Production did not confirm this browser subscription.');
    } catch (enableError) {
      if (window.Notification?.permission === 'denied') setState('permission_blocked');
      else setState('not_enabled');
      setError(enableError?.response?.data?.error || enableError?.message || 'Browser alerts could not be enabled.');
    }
  };

  const handleTest = async () => {
    setTestState('sending');
    setTestMessage('Sending a test notification...');
    try {
      const result = await sendWebPushTest(email, endpoint);
      if (!result?.ok || !result.test_id) throw new Error('Test notification was not accepted.');
      setTestMessage('Provider accepted the test. Waiting for this browser to confirm receipt...');
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        const status = await fetchWebPushStatus(email, endpoint, result.test_id);
        if (status?.test?.browser_received_at) {
          setTestState('received');
          setState('verified');
          setTestMessage(`Test received by this browser at ${new Date(status.test.browser_received_at).toLocaleTimeString()}.`);
          return;
        }
        if (status?.test?.provider_status === 'failed') {
          throw new Error(status.test.error || 'The push provider rejected the test.');
        }
      }
      setTestState('accepted');
      setState('enrolled');
      setTestMessage('The provider accepted the test, but this browser did not receive it. Repair browser alerts before relying on this backup channel.');
    } catch (testError) {
      setTestState('failed');
      setTestMessage(testError?.response?.data?.error || testError?.message || 'Test notification failed.');
    }
  };

  const handleRepair = async () => {
    setTestState('repairing');
    setTestMessage('Replacing this device\'s browser alert subscription...');
    try {
      const result = await repairWebPush(email);
      if (!result?.ok || !result.endpoint) throw new Error('Browser alert repair was not confirmed.');
      setEndpoint(result.endpoint);
      setState('enrolled');
      setTestState('idle');
      setTestMessage('Fresh browser subscription created. Send a test notification to verify delivery.');
    } catch (repairError) {
      setTestState('failed');
      setState('enrolled');
      setTestMessage(repairError?.response?.data?.error || repairError?.message || 'Browser alert repair failed.');
    }
  };

  if (!paid || !email) return null;

  if (compact && (state === 'checking' || state === 'verified' || state === 'unsupported')) {
    return null;
  }

  if (state === 'checking') {
    return (
      <div className="subscription-banner info" role="status">
        <span className="banner-copy">Checking browser alert status...</span>
      </div>
    );
  }

  if (state === 'unsupported') {
    return (
      <div className="subscription-banner warning">
        <span className="banner-copy">
          Browser alerts are unavailable in this browser. Email visa-decision alerts remain active.
        </span>
      </div>
    );
  }

  if (state === 'permission_blocked') {
    return (
      <div className="subscription-banner warning" role="status">
        <span className="banner-copy">
          Permission blocked. Allow notifications for irelandvisatracker.in in your browser settings, then reload this page.
        </span>
      </div>
    );
  }

  if (state === 'verified') {
    return (
      <div className="subscription-banner success">
        <span className="banner-copy">
          Browser alerts verified on this device. Visa decisions can use web push as an email backup.
        </span>
        <button type="button" className="browser-alert-test-button" onClick={handleTest} disabled={testState === 'sending'}>
          {testState === 'sending' ? 'Testing...' : 'Send test notification'}
        </button>
        {testMessage && <span className="banner-copy" role="status">{testMessage}</span>}
      </div>
    );
  }

  if (state === 'enrolled') {
    const busy = testState === 'sending' || testState === 'repairing';
    return (
      <div className="subscription-banner warning" role="status">
        <span className="banner-copy">
          {compact
            ? 'Browser alerts need delivery verification on this device.'
            : 'Browser alerts are enrolled, but this device has not confirmed delivery yet.'}
        </span>
        {!compact && (
          <button type="button" className="browser-alert-test-button" onClick={handleTest} disabled={busy}>
            {testState === 'sending' ? 'Testing...' : 'Send test notification'}
          </button>
        )}
        <button type="button" className="browser-alert-repair-button" onClick={handleRepair} disabled={busy}>
          {testState === 'repairing' ? 'Repairing...' : 'Repair browser alerts'}
        </button>
        {testMessage && <span className="banner-copy" role="status">{testMessage}</span>}
      </div>
    );
  }

  return (
    <div className={`subscription-banner browser-alert-enrollment${compact ? ' browser-alert-enrollment--compact' : ''}`}>
      <span className="banner-copy">
        {state === 'error'
          ? 'Registration error. Production could not confirm browser alert status.'
          : compact
            ? 'Do not miss your visa decision. Add browser alerts as an instant backup to email.'
            : 'Not enabled on this device. Enable browser notifications as a backup channel in case email is delayed.'}
      </span>
      <button type="button" className="browser-alert-enable-button" onClick={handleEnable}>
        Enable browser alerts
      </button>
      {error && <span className="banner-copy">{error}</span>}
    </div>
  );
};

export default BrowserAlertStatus;
